import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import s from './PedidosOrcamentos.module.css';
import ImportacaoXMLWizard from './ImportacaoXMLWizard';
import RevisaoFiscalNF from './RevisaoFiscalNF';
import ModalDfe from '../components/fiscal/ModalDfe';
import ModalSimulacaoPrecos from '../components/fiscal/ModalSimulacaoPrecos';
import ModalDevolucaoCompra from '../components/fiscal/ModalDevolucaoCompra';
import DevolucaoCompraDetalhe from '../components/fiscal/DevolucaoCompraDetalhe';
import { abrirDanfeNfePdf, fetchLogoBase64, fecharComEsc, aguardarImagens } from '../utils/impressaoVenda';
import { hojeBR } from '../utils/data';
import { useAuth } from '../contexts/AuthContext';

/* ── Helpers ── */
const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData  = str => str ? new Date(String(str).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';
const fmtDT    = str => str ? new Date(str).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const sv       = v => (v == null ? '' : String(v));

const TIPO_FRETE_OPTS = [
  { v: '0', l: '0 — Emitente (CIF)' },
  { v: '1', l: '1 — Destinatário (FOB)' },
  { v: '2', l: '2 — Terceiros' },
  { v: '3', l: '3 — Próprio/Remetente' },
  { v: '9', l: '9 — Sem frete' },
];

const STATUS_BADGE = {
  pendente:    { cls: s.badgeAberto,    label: 'Pendente' },
  confirmada:  { cls: s.badgeAprovado,  label: 'Confirmada' },
  cancelada:   { cls: s.badgeCancelado, label: 'Cancelada' },
  rascunho:    { cls: s.badgeAberto,    label: 'Rascunho' },
  transmitida: { cls: s.badgeAprovado,  label: 'Transmitida' },
};

const FORM_VAZIO = {
  id: null, codigo: '', fornecedor_id: null, fornecedor_nome: '',
  numero_nf: '', serie: '', chave_acesso: '',
  data_emissao: '', data_entrada: hojeBR(),
  natureza_operacao: '',
  valor_produtos: '0', valor_frete: '0', valor_seguro: '0',
  valor_desconto: '0', valor_ipi: '0', valor_icms_st: '0',
  valor_icms: '0', valor_total: '0',
  tipo_frete: '9', transportadora: '',
  status: 'pendente',
  xml_original: null,
  itens: [],
};

const entradaParaForm = (e) => ({
  id:               e.id || null,
  codigo:           sv(e.codigo),
  fornecedor_id:    e.fornecedor_id || null,
  fornecedor_nome:  e.fornecedor_nome || '',
  numero_nf:        e.numero_nf || '',
  serie:            e.serie || '',
  chave_acesso:     e.chave_acesso || '',
  data_emissao:     e.data_emissao ? e.data_emissao.slice(0, 10) : '',
  data_entrada:     e.data_entrada ? e.data_entrada.slice(0, 10) : hojeBR(),
  natureza_operacao: e.natureza_operacao || '',
  valor_produtos:   sv(e.valor_produtos || 0),
  valor_frete:      sv(e.valor_frete || 0),
  valor_seguro:     sv(e.valor_seguro || 0),
  valor_desconto:   sv(e.valor_desconto || 0),
  valor_ipi:        sv(e.valor_ipi || 0),
  valor_icms_st:    sv(e.valor_icms_st || 0),
  valor_icms:       sv(e.valor_icms || 0),
  valor_total:      sv(e.valor_total || 0),
  tipo_frete:       e.tipo_frete || '9',
  transportadora:   e.transportadora || '',
  status:           e.status || 'pendente',
  xml_original:     e.xml_original || null,
  tipo_movimento_id:     e.tipo_movimento_id || null,
  tipo_movimento_nome:   e.tipo_movimento_nome || '',
  tmf_movimenta_estoque: e.tmf_movimenta_estoque,
  itens: (e.itens || []).map((it, i) => ({
    _key:              it.id || Date.now() + i,
    produto_id:        it.produto_id || null,
    produto_nome:      it.produto_nome || '',
    produto_codigo:    it.codigo_interno || '',
    produto_inativo:   !!it.produto_inativo,
    codigo_fornecedor: it.codigo_fornecedor || '',
    descricao:         it.descricao || '',
    ncm:               it.ncm || '',
    cfop:              it.cfop || '',
    csosn:             it.csosn || '',
    unidade:           it.unidade || '',
    quantidade:        sv(it.quantidade || 0),
    valor_unitario:    sv(it.valor_unitario || 0),
    valor_total:       sv(it.valor_total || 0),
    valor_ipi:         sv(it.valor_ipi || 0),
    valor_icms:        sv(it.valor_icms || 0),
    valor_icms_st:     sv(it.valor_icms_st || 0),
    desconto:          sv(it.desconto || 0),
  })),
});

/* ── Modal Busca de Fornecedor (inline) ── */
function ModalBuscaFornecedor({ onSelect, onClose }) {
  const [busca, setBusca]   = useState('');
  const [lista, setLista]   = useState([]);
  const [load,  setLoad]    = useState(false);
  const [selIdx, setSelIdx] = useState(null);

  const pesquisar = async (txt) => {
    setLoad(true); setSelIdx(null);
    try {
      const { data } = await api.get('/fornecedores', { params: txt ? { busca: txt } : {} });
      setLista(Array.isArray(data) ? data : []);
    } catch { setLista([]); }
    finally { setLoad(false); }
  };

  useEffect(() => { pesquisar(''); }, []);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && selIdx !== null) onSelect(lista[selIdx]);
      if (e.key === 'ArrowDown') setSelIdx(i => i === null ? 0 : Math.min(i + 1, lista.length - 1));
      if (e.key === 'ArrowUp')   setSelIdx(i => i === null ? 0 : Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [selIdx, lista]); // eslint-disable-line

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal} style={{ width: 640 }}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Selecionar Fornecedor</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 15 }}
            onClick={onClose}>✕</button>
        </div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input className={s.inputErp} style={{ flex: 1 }}
              placeholder="Nome fantasia, razão social, CNPJ..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && pesquisar(busca)}
              autoFocus />
            <button className={s.btnErp} onClick={() => pesquisar(busca)}>
              {load ? '...' : 'Buscar'}
            </button>
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 300, border: '1px solid #ccc' }}>
            <table className={s.modalTabela}>
              <thead><tr>
                <th style={{ width: 50 }}>Cód.</th>
                <th>Nome Fantasia</th>
                <th>Razão Social</th>
                <th style={{ width: 130 }}>CNPJ</th>
              </tr></thead>
              <tbody>
                {lista.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 12, color: '#888', fontStyle: 'italic' }}>
                    {load ? 'Buscando...' : 'Nenhum resultado.'}
                  </td></tr>
                )}
                {lista.map((f, i) => (
                  <tr key={f.id}
                    className={selIdx === i ? s.selRow : ''}
                    onClick={() => setSelIdx(i)}
                    onDoubleClick={() => onSelect(f)}>
                    <td>{f.codigo}</td>
                    <td>{f.nome_fantasia}</td>
                    <td>{f.razao_social}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{f.cpf_cnpj}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            disabled={selIdx === null}
            onClick={() => selIdx !== null && onSelect(lista[selIdx])}>Selecionar</button>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Busca de Produto ── */
function ModalBuscaProduto({ onSelect, onClose }) {
  const [buscaRapida, setBuscaRapida] = useState('');
  const [produtos, setProdutos]       = useState([]);
  const [buscando, setBuscando]       = useState(false);
  const [idx, setIdx]                 = useState(null);
  const inputRef = useRef(null);
  const debRef   = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 60); }, []);

  const buscar = async (texto) => {
    setBuscando(true); setIdx(null);
    try {
      const { data } = await api.get('/produtos', { params: texto ? { busca: texto, limite: 200 } : { limite: 50 } });
      setProdutos(Array.isArray(data) ? data : []);
    } catch { setProdutos([]); }
    finally { setBuscando(false); }
  };

  const handleRapida = e => {
    const v = e.target.value;
    setBuscaRapida(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => buscar(v), 300);
  };

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && idx !== null) { e.preventDefault(); onSelect(produtos[idx]); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => i === null ? 0 : Math.min(i + 1, produtos.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => i === null ? 0 : Math.max(i - 1, 0)); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [idx, produtos]); // eslint-disable-line

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modalF3}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Selecionar Produto</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}
            onClick={onClose}>✕</button>
        </div>
        <div className={s.f3GridWrap}>
          <table className={s.f3Grid}>
            <thead><tr>
              <th style={{ width: 85 }}>Código</th>
              <th>Descrição</th>
              <th style={{ width: 42 }}>UN</th>
              <th style={{ width: 90, textAlign: 'right' }}>Estoque</th>
            </tr></thead>
            <tbody>
              {produtos.length === 0 && !buscando && (
                <tr><td colSpan={4} className={s.f3Vazio}>Use a busca rápida abaixo.</td></tr>
              )}
              {buscando && (
                <tr><td colSpan={4} className={s.f3Vazio}>Buscando...</td></tr>
              )}
              {produtos.map((p, i) => (
                <tr key={p.id}
                  className={idx === i ? s.f3LinhaSel : ''}
                  onClick={() => setIdx(i)}
                  onDoubleClick={() => onSelect(p)}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.codigo_interno}</td>
                  <td>{p.nome}</td>
                  <td>{p.unidade || ''}</td>
                  <td style={{ textAlign: 'right' }}>{Number(p.estoque_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.f3Rodape}>
          <div className={s.f3BuscaRapidaWrap}>
            <label className={s.f3Label}>Busca:</label>
            <input ref={inputRef} className={s.f3BuscaRapida}
              placeholder="Nome ou código..."
              value={buscaRapida} onChange={handleRapida}
              autoComplete="off" />
          </div>
          <div className={s.f3BotoesRodape}>
            <button className={s.f3BtnOk}
              disabled={idx === null}
              onClick={() => idx !== null && onSelect(produtos[idx])}>Ok</button>
            <button className={s.f3BtnSair} onClick={onClose}>Sair</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   Componente Principal
══════════════════════════════════ */
export default function EntradaNF({ abrirDfe = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario } = useAuth();

  /* ── Dados da organização (logo/CNPJ/endereço p/ impressão) ── */
  const [orgData, setOrgData] = useState(null);
  useEffect(() => {
    if (usuario?.organizacao_id)
      api.get(`/organizacoes/${usuario.organizacao_id}`).then(r => setOrgData(r.data)).catch(() => {});
  }, [usuario]);

  /* ── View state ── */
  const [modo, setModo]           = useState('lista'); // 'lista' | 'form'
  const [abaAtiva, setAbaAtiva]   = useState('cabecalho');

  /* ── Lista ── */
  const [lista, setLista]         = useState([]);
  const [busca, setBusca]         = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [selId, setSelId]         = useState(null);
  const [carregando, setCarregando] = useState(false);

  /* ── Lista de Devoluções de Compra (aba separada da lista de notas) ── */
  const [tipoLista, setTipoLista]           = useState('entradas'); // 'entradas' | 'devolucoes'
  const [devolucoesLista, setDevolucoesLista] = useState([]);
  const [carregandoDev, setCarregandoDev]   = useState(false);
  const [selDevId, setSelDevId]             = useState(null);
  const [devolucaoAbertaId, setDevolucaoAbertaId] = useState(null); // consulta/navegação (DevolucaoCompraDetalhe)

  /* ── Form ── */
  const [form, setForm]           = useState({ ...FORM_VAZIO });
  const [editando, setEditando]   = useState(false);
  const [salvando, setSalvando]   = useState(false);
  const [erroMsg, setErroMsg]     = useState('');

  /* ── Modais ── */
  const [modalNovo,       setModalNovo]       = useState(false);
  const [modalDfe,        setModalDfe]        = useState(abrirDfe);
  // Devolução de compra (processo completo em componente próprio)
  const [devolucaoEntradaId, setDevolucaoEntradaId] = useState(null);
  const [modalFornec,     setModalFornec]     = useState(false);
  const [modalProdutoIdx, setModalProdutoIdx] = useState(null);
  const [modalConfirmar,  setModalConfirmar]  = useState(false);
  const [atualizarEstoque, setAtualizarEstoque] = useState(true);
  const [confirmando, setConfirmando]         = useState(false);

  /* ── Duplicatas (contas a pagar vinculadas à NF) ── */
  const [duplicatas, setDuplicatas]         = useState([]);
  const [duplicatasLoad, setDuplicatasLoad] = useState(false);

  /* ── XML ref ── */
  const xmlInputRef = useRef(null);

  /* ── Wizard de importação XML ── */
  const [wizardXml, setWizardXml] = useState(null);

  /* ── Carregar lista ── */
  const carregarLista = useCallback(async () => {
    setCarregando(true);
    try {
      const params = {};
      if (busca) params.busca = busca;
      if (filtroStatus) params.status = filtroStatus;
      const { data } = await api.get('/entradas-nf', { params });
      setLista(Array.isArray(data) ? data : []);
    } catch { setLista([]); }
    finally { setCarregando(false); }
  }, [busca, filtroStatus]);

  useEffect(() => { carregarLista(); }, [carregarLista]);

  const carregarDevolucoes = useCallback(async () => {
    setCarregandoDev(true);
    try {
      const { data } = await api.get('/devolucoes-compra');
      setDevolucoesLista(Array.isArray(data) ? data : []);
    } catch { setDevolucoesLista([]); }
    finally { setCarregandoDev(false); }
  }, []);

  useEffect(() => { if (tipoLista === 'devolucoes') carregarDevolucoes(); }, [tipoLista, carregarDevolucoes]);

  /* ── Carregar duplicatas ao abrir aba ── */
  useEffect(() => {
    if (abaAtiva !== 'duplicatas' || !form.id) return;
    setDuplicatasLoad(true);
    api.get('/financeiro/contas-pagar', { params: { entrada_nf_id: form.id } })
      .then(r => setDuplicatas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDuplicatas([]))
      .finally(() => setDuplicatasLoad(false));
  }, [abaAtiva, form.id]); // eslint-disable-line

  /* ── Abrir entrada ── */
  const abrirEntrada = async (id) => {
    try {
      const { data } = await api.get(`/entradas-nf/${id}`);
      setForm(entradaParaForm(data));
      setEditando(false);
      setErroMsg('');
      setAbaAtiva('cabecalho');
      setModo('form');
    } catch { alert('Erro ao carregar entrada.'); }
  };

  /* ── Deep-link: chegou aqui via "Últimas Compras" do cadastro de produtos ── */
  useEffect(() => {
    const id = location.state?.abrirEntradaId;
    if (!id) return;
    abrirEntrada(id);
    // Limpa o state para não reabrir se o usuário navegar de volta a esta rota
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state]); // eslint-disable-line

  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  /* ── Novo form manual ── */
  const abrirFormManual = () => {
    setForm({ ...FORM_VAZIO, data_entrada: hojeBR() });
    setEditando(true);
    setErroMsg('');
    setAbaAtiva('cabecalho');
    setModo('form');
    setModalNovo(false);
  };

  /* ── Import XML (abre o wizard) ── */
  const processarXml = async (xmlContent) => {
    try {
      const { data } = await api.post('/entradas-nf/importar-xml', { xml: xmlContent });
      if (data.validacao?.erros?.length > 0 && !data.validacao?.xml_valido) {
        alert('Erro no XML: ' + data.validacao.erros.join(', '));
        return false;
      }
      setWizardXml({ data, xmlContent });
      return true;
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao processar XML.');
      return false;
    }
  };

  const handleXmlFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setModalNovo(false);
    const reader = new FileReader();
    reader.onload = (ev) => processarXml(ev.target.result);
    reader.readAsText(file, 'UTF-8');
  };

  /* ── Import via DF-e (mesmo wizard do XML) ── */
  const handleDfeImportar = async (xmlContent) => {
    const ok = await processarXml(xmlContent);
    if (ok) setModalDfe(false);
  };

  /* ── Confirmação do wizard: salva direto no banco ── */
  const handleWizardConfirmar = async (payload) => {
    const d = payload.dados || {};
    const t = payload.transporte || {};
    const itens = (payload.itens || []).map(it => ({
      produto_id:        it.produto_id || null,
      codigo_fornecedor: it.codigo_fornecedor || '',
      descricao:         it.descricao || '',
      ncm:               it.ncm || '',
      cfop:              it.cfop || '',
      csosn:             it.csosn || '',
      unidade:           it.unidade || '',
      quantidade:        parseFloat(it.quantidade || 0),
      valor_unitario:    parseFloat(it.valor_unitario || 0),
      valor_total:       parseFloat(it.valor_total || 0),
      valor_ipi:         parseFloat(it.valor_ipi || 0),
      valor_icms:        parseFloat(it.valor_icms || 0),
      valor_icms_st:     parseFloat(it.valor_icms_st || 0),
      desconto:          parseFloat(it.desconto || 0),
    }));

    const savePayload = {
      fornecedor_id:     payload.fornecedor_id || null,
      tipo_movimento_id: payload.tipo_movimento_id || null,
      numero_nf:         d.numero_nf,
      serie:             d.serie,
      chave_acesso:      d.chave_acesso,
      data_emissao:      d.data_emissao || null,
      data_entrada:      d.data_entrada || null,
      natureza_operacao: d.natureza_operacao,
      valor_produtos:    parseFloat(d.valor_produtos || 0),
      valor_frete:       parseFloat(d.valor_frete || 0),
      valor_seguro:      parseFloat(d.valor_seguro || 0),
      valor_desconto:    parseFloat(d.valor_desconto || 0),
      valor_ipi:         parseFloat(d.valor_ipi || 0),
      valor_icms_st:     parseFloat(d.valor_icms_st || 0),
      valor_icms:        parseFloat(d.valor_icms || 0),
      valor_total:       parseFloat(d.valor_total || 0),
      tipo_frete:        t.tipo_frete || '9',
      transportadora:    t.transportadora || '',
      status:            'pendente',
      xml_original:      payload.xml_original || null,
      itens,
    };

    try {
      const { data: saved } = await api.post('/entradas-nf', savePayload);
      setWizardXml(null);
      await abrirEntrada(saved.id);
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao salvar a entrada de NF.');
    }
  };

  /* ── Alterar / Cancelar ── */
  const handleAlterar   = () => { setEditando(true); setErroMsg(''); };
  const handleCancelar  = () => {
    setEditando(false);
    setErroMsg('');
    if (form.id) {
      abrirEntrada(form.id);
    } else {
      setModo('lista');
    }
  };

  /* ── Salvar ── */
  const handleSalvar = async () => {
    setSalvando(true);
    setErroMsg('');
    try {
      const payload = {
        fornecedor_id:     form.fornecedor_id,
        numero_nf:         form.numero_nf,
        serie:             form.serie,
        chave_acesso:      form.chave_acesso,
        data_emissao:      form.data_emissao || null,
        data_entrada:      form.data_entrada || null,
        natureza_operacao: form.natureza_operacao,
        valor_produtos:    parseFloat(form.valor_produtos || 0),
        valor_frete:       parseFloat(form.valor_frete || 0),
        valor_seguro:      parseFloat(form.valor_seguro || 0),
        valor_desconto:    parseFloat(form.valor_desconto || 0),
        valor_ipi:         parseFloat(form.valor_ipi || 0),
        valor_icms_st:     parseFloat(form.valor_icms_st || 0),
        valor_icms:        parseFloat(form.valor_icms || 0),
        valor_total:       parseFloat(form.valor_total || 0),
        tipo_frete:        form.tipo_frete,
        transportadora:    form.transportadora,
        status:            form.status,
        xml_original:      form.xml_original || null,
        itens: form.itens.map(it => ({
          produto_id:        it.produto_id,
          codigo_fornecedor: it.codigo_fornecedor,
          descricao:         it.descricao,
          ncm:               it.ncm,
          cfop:              it.cfop,
          csosn:             it.csosn,
          unidade:           it.unidade,
          quantidade:        parseFloat(it.quantidade || 0),
          valor_unitario:    parseFloat(it.valor_unitario || 0),
          valor_total:       parseFloat(it.valor_total || 0),
          valor_ipi:         parseFloat(it.valor_ipi || 0),
          valor_icms:        parseFloat(it.valor_icms || 0),
          valor_icms_st:     parseFloat(it.valor_icms_st || 0),
          desconto:          parseFloat(it.desconto || 0),
        })),
      };

      let savedId;
      if (form.id) {
        await api.put(`/entradas-nf/${form.id}`, payload);
        savedId = form.id;
      } else {
        const { data } = await api.post('/entradas-nf', payload);
        savedId = data.id;
      }
      await abrirEntrada(savedId);
      setEditando(false);
      carregarLista();
    } catch (err) {
      setErroMsg(err.response?.data?.erro || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  };

  /* ── Excluir ── */
  const handleExcluir = async () => {
    const id = modo === 'lista' ? selId : form.id;
    if (!id) return;
    if (!window.confirm('Excluir esta entrada de NF?')) return;
    try {
      await api.delete(`/entradas-nf/${id}`);
      if (modo === 'form') {
        setModo('lista');
        await carregarLista();
      } else {
        setSelId(null);
        await carregarLista();
      }
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  const handleExcluirDevolucao = async () => {
    if (!selDevId) return;
    const dev = devolucoesLista.find(d => d.id === selDevId);
    if (!window.confirm(
      dev?.status === 'confirmada'
        ? 'Excluir esta devolução? O estoque baixado e o crédito do fornecedor serão revertidos.'
        : 'Excluir este rascunho de devolução?'
    )) return;
    try {
      await api.delete(`/devolucoes-compra/${selDevId}`);
      setSelDevId(null);
      await carregarDevolucoes();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  /* ── Imprimir NF (cabeçalho + itens, com preço de venda atualizado) ──
     Busca a entrada direto da API (funciona tanto a partir da lista quanto
     do detalhe já aberto) pra sempre trazer o preço de venda mais recente
     do cadastro, não o valor de custo gravado na nota. */
  const [imprimindoNF, setImprimindoNF] = useState(false);
  const imprimirNF = async (id) => {
    if (!id || imprimindoNF) return;
    setImprimindoNF(true);
    try {
      const { data: e } = await api.get(`/entradas-nf/${id}`);
      const org = orgData || {};
      const logoData = await fetchLogoBase64(org.logo_url || '');
      const logoHtml = logoData
        ? `<div style="margin-bottom:8px;"><img src="${logoData}" style="max-height:70px;max-width:180px;object-fit:contain;" /></div>`
        : '';
      const nome = org.nome_fantasia || org.razao_social || 'EMPRESA';
      const cnpj = (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      const end  = [org.logradouro, org.numero, org.complemento, org.bairro].filter(Boolean).join(', ');
      const cid  = [org.cidade, org.uf].filter(Boolean).join('/');
      const fone = org.telefone ? `Tel: ${org.telefone}` : '';

      const itens = e.itens || [];
      let totalVendaAtualizado = 0;
      const linhasItens = itens.map((it, idx) => {
        const qtd = parseFloat(it.quantidade || 0);
        const temPreco = it.preco_venda_atual != null;
        const vVenda = temPreco ? parseFloat(it.preco_venda_atual) : null;
        if (vVenda != null) totalVendaAtualizado += qtd * vVenda;
        return `<tr>
          <td class="c">${idx + 1}</td>
          <td class="c" style="font-family:monospace;">${it.codigo_interno || '—'}</td>
          <td>${it.produto_nome || it.descricao || '—'}</td>
          <td class="c">${qtd.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}${it.unidade ? ' ' + it.unidade : ''}</td>
          <td class="r">${vVenda != null ? fmtMoeda(vVenda) : '—'}</td>
        </tr>`;
      }).join('');

      const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Entrada de NF ${e.numero_nf || e.codigo}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:Arial,sans-serif;font-size:12pt;padding:20px;color:#111;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;border-bottom:2px solid #333;padding-bottom:10px;}
  .org-info{flex:1;}
  .doc-info{text-align:right;min-width:200px;}
  .doc-info h2{font-size:16pt;color:#333;}
  .cabecalho{margin-bottom:10px;padding:8px;background:#f5f5f5;border-radius:4px;font-size:11pt;}
  .cabecalho div{margin-bottom:2px;}
  table{width:100%;border-collapse:collapse;margin:10px 0;}
  th{background:#333;color:#fff;padding:5px 8px;text-align:left;font-size:11pt;}
  td{padding:4px 8px;border-bottom:1px solid #ddd;font-size:11pt;}
  .r{text-align:right;}.c{text-align:center;}
  tfoot td{border-top:2px solid #333;border-bottom:none;font-weight:bold;font-size:12pt;}
  .footer{margin-top:20px;border-top:1px solid #ccc;padding-top:8px;font-size:10pt;color:#777;text-align:center;}
  @media print{@page{margin:15mm;size:A4 portrait;}}
</style></head><body>
  <div class="header">
    <div class="org-info">
      ${logoHtml}
      <div style="font-weight:bold;font-size:14pt;">${nome}</div>
      ${cnpj ? `<div>CNPJ: ${cnpj}</div>` : ''}
      ${end  ? `<div>${end}</div>` : ''}
      ${cid  ? `<div>${cid}</div>` : ''}
      ${fone ? `<div>${fone}</div>` : ''}
    </div>
    <div class="doc-info">
      <h2>ENTRADA DE NF</h2>
      <div><b>Nº Nota:</b> ${e.numero_nf || '—'}</div>
      <div><b>Série:</b> ${e.serie || '—'}</div>
      <div><b>Status:</b> ${STATUS_BADGE[e.status]?.label || e.status}</div>
    </div>
  </div>

  <div class="cabecalho">
    <div><b>Fornecedor:</b> ${e.fornecedor_nome || '—'}</div>
    <div><b>Data Emissão:</b> ${fmtData(e.data_emissao)} &nbsp;|&nbsp; <b>Data Entrada:</b> ${fmtData(e.data_entrada)}</div>
    ${e.natureza_operacao ? `<div><b>Natureza da Operação:</b> ${e.natureza_operacao}</div>` : ''}
    ${e.chave_acesso ? `<div style="font-family:monospace;font-size:9.5pt;"><b>Chave de Acesso:</b> ${e.chave_acesso}</div>` : ''}
    <div><b>Valor Total da Nota:</b> ${fmtMoeda(e.valor_total)}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:5%" class="c">#</th>
        <th style="width:14%">Cód. Interno</th>
        <th>Descrição Interna</th>
        <th style="width:16%" class="c">Quantidade</th>
        <th style="width:18%" class="r">Vl. Venda Atualizado</th>
      </tr>
    </thead>
    <tbody>
      ${linhasItens || `<tr><td colspan="5" style="text-align:center;color:#888;font-style:italic;">Nenhum item.</td></tr>`}
    </tbody>
    <tfoot>
      <tr><td colspan="4" class="r">Total (valor de venda atualizado):</td><td class="r">${fmtMoeda(totalVendaAtualizado)}</td></tr>
    </tfoot>
  </table>

  <div class="footer">Documento gerado em ${new Date().toLocaleString('pt-BR')} — ${nome}</div>
</body></html>`;

      const win = window.open('', '_blank', 'width=900,height=900');
      if (!win) { alert('Permita pop-ups para imprimir.'); return; }
      win.document.write(html);
      win.document.close();
      fecharComEsc(win);
      await aguardarImagens(win);
      win.focus();
      win.print();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao gerar impressão da NF.');
    } finally {
      setImprimindoNF(false);
    }
  };

  /* ── Confirmar NF ── */
  // TMF com movimenta_estoque=false (ex.: uso e consumo) nunca atualiza estoque
  const tmfSemEstoque = form.tmf_movimenta_estoque === false;
  const handleConfirmar = async () => {
    setConfirmando(true);
    try {
      const { data } = await api.post(`/entradas-nf/${form.id}/confirmar`,
        { atualizar_estoque: tmfSemEstoque ? false : atualizarEstoque });
      setModalConfirmar(false);
      const reativados = data?.produtos_reativados || [];
      if (reativados.length) {
        alert(`${reativados.length} produto(s) inativo(s) voltaram a ser ativos:\n\n` +
          reativados.map(p => `[${p.codigo_interno || p.id}] ${p.nome}`).join('\n'));
      }
      await abrirEntrada(form.id);
      carregarLista();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao confirmar NF.');
    } finally {
      setConfirmando(false);
    }
  };

  /* ── Cancelar Confirmação da NF ── */
  const [modalCancelarConf, setModalCancelarConf] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [revisaoFiscalId, setRevisaoFiscalId] = useState(null);
  const [modalPrecosId, setModalPrecosId] = useState(null); // simulação de preços da nota // id da NF aberta para revisão
  const handleCancelarConfirmacao = async () => {
    setCancelando(true);
    try {
      await api.post(`/entradas-nf/${form.id}/cancelar-confirmacao`);
      setModalCancelarConf(false);
      await abrirEntrada(form.id);
      carregarLista();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao cancelar confirmação.');
    } finally {
      setCancelando(false);
    }
  };

  /* ── Adicionar / remover item ── */
  const handleAddItem = () => {
    const novo = {
      _key: Date.now(),
      produto_id: null, produto_nome: '',
      codigo_fornecedor: '', descricao: '',
      ncm: '', cfop: '', csosn: '', unidade: 'UN',
      quantidade: '1', valor_unitario: '0',
      valor_total: '0', valor_ipi: '0',
      valor_icms: '0', valor_icms_st: '0', desconto: '0',
    };
    setForm(f => ({ ...f, itens: [...f.itens, novo] }));
  };

  const handleRemoveItem = (key) => {
    setForm(f => ({ ...f, itens: f.itens.filter(it => it._key !== key) }));
  };

  const setItem = (key, campo, valor) => {
    setForm(f => ({
      ...f,
      itens: f.itens.map(it => {
        if (it._key !== key) return it;
        const upd = { ...it, [campo]: valor };
        if (campo === 'quantidade' || campo === 'valor_unitario') {
          const q = parseFloat(campo === 'quantidade' ? valor : upd.quantidade) || 0;
          const v = parseFloat(campo === 'valor_unitario' ? valor : upd.valor_unitario) || 0;
          upd.valor_total = sv((q * v).toFixed(2));
        }
        return upd;
      }),
    }));
  };

  /* ── Status badge ── */
  const renderBadge = (status) => {
    const b = STATUS_BADGE[status] || STATUS_BADGE.pendente;
    return <span className={`${s.badge} ${b.cls}`}>{b.label}</span>;
  };

  /* ── Status text ── */
  const statusText = () => {
    if (erroMsg) return <span className={s.statusErro}>{erroMsg}</span>;
    if (!form.id && editando) return <span className={s.statusNovo}>Nova Entrada de NF</span>;
    if (editando) return <span className={s.statusEditar}>Editando NF {form.numero_nf || '—'}</span>;
    if (form.id) return `NF ${form.numero_nf || '—'} | ${form.fornecedor_nome || 'Sem fornecedor'} | ${renderBadge(form.status)}`;
    return '';
  };

  /* ═══════════════════════════════
     VIEW DEVOLUÇÃO — consulta/navegação (fora do modo lista/form da entrada)
  ═══════════════════════════════ */
  if (devolucaoAbertaId) {
    return (
      <DevolucaoCompraDetalhe
        devolucaoId={devolucaoAbertaId}
        onClose={() => setDevolucaoAbertaId(null)}
        onAlterou={carregarDevolucoes}
      />
    );
  }

  /* ═══════════════════════════════
     VIEW LISTA
  ═══════════════════════════════ */
  if (modo === 'lista') {
    return (
      <>
      {wizardXml && (
        <ImportacaoXMLWizard
          xmlData={wizardXml.data}
          xmlContent={wizardXml.xmlContent}
          onConfirmar={handleWizardConfirmar}
          onCancelar={() => setWizardXml(null)}
        />
      )}
      <div className={s.erpWrap}>
        <div className={s.titleBar}>
          <span>📥</span> Entrada de Nota Fiscal
        </div>
        <div className={s.tabContent}>
          {/* Notas de Entrada × Devoluções de Compra */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: '2px solid #1464c8' }}>
            {[['entradas', 'Notas de Entrada'], ['devolucoes', 'Devoluções de Compra']].map(([id, label]) => (
              <button key={id} onClick={() => setTipoLista(id)}
                style={{
                  padding: '6px 16px', border: 'none', borderRight: '1px solid #bbb',
                  background: tipoLista === id ? '#fff' : '#e0e0e0',
                  fontWeight: tipoLista === id ? 'bold' : 'normal',
                  color: tipoLista === id ? '#0a3a8c' : '#333',
                  cursor: 'pointer', fontSize: 13,
                }}>
                {label}
              </button>
            ))}
          </div>

          {tipoLista === 'entradas' ? (<>
            {/* Barra de busca */}
            <div style={{ marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input className={s.inputErp} style={{ flex: 1 }}
                placeholder="Buscar por fornecedor, número NF..."
                value={busca}
                onChange={e => setBusca(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && carregarLista()} />
              <select className={s.selectErp}
                value={filtroStatus}
                onChange={e => setFiltroStatus(e.target.value)}>
                <option value="">Todos os status</option>
                <option value="pendente">Pendente</option>
                <option value="confirmada">Confirmada</option>
                <option value="cancelada">Cancelada</option>
              </select>
              <button className={s.btnErp} onClick={carregarLista}>Atualizar</button>
            </div>

            {/* Tabela */}
            <table className={s.itensGrid}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Cód.</th>
                  <th>Fornecedor</th>
                  <th style={{ width: 80 }}>NF</th>
                  <th style={{ width: 50 }}>Série</th>
                  <th style={{ width: 100 }}>Data Entrada</th>
                  <th style={{ width: 130 }} title="Data e hora em que o registro foi gravado no sistema">Data/Hora Operação</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Valor Total</th>
                  <th style={{ width: 90 }}>Status</th>
                  <th style={{ width: 130 }}>Layout</th>
                  <th style={{ width: 60, textAlign: 'center' }}
                    title="Preços de venda dos produtos desta nota já atualizados pela Simulação de Preços">
                    Preços
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#888', fontStyle: 'italic' }}>
                      {carregando ? 'Carregando...' : 'Nenhuma entrada encontrada.'}
                    </td>
                  </tr>
                )}
                {lista.map(e => (
                  <tr key={e.id}
                    style={{ cursor: 'pointer' }}
                    className={selId === e.id ? s.linhaAtiva : ''}
                    onClick={() => setSelId(e.id)}
                    onDoubleClick={() => abrirEntrada(e.id)}>
                    <td style={{ textAlign: 'center' }}>{e.codigo}</td>
                    <td>{e.fornecedor_nome || '—'}</td>
                    <td>{e.numero_nf || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{e.serie || '—'}</td>
                    <td>{fmtData(e.data_entrada)}</td>
                    <td style={{ fontSize: 11.5 }}>{fmtDT(e.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoeda(e.valor_total)}</td>
                    <td>{renderBadge(e.status)}</td>
                    <td style={{ fontSize: 11.5, color: '#555' }}>{e.tipo_movimento_nome || '—'}</td>
                    <td style={{ textAlign: 'center' }}
                      title={e.precos_atualizados_em
                        ? 'Preços atualizados em ' + new Date(e.precos_atualizados_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                        : e.tem_precos_pendentes
                          ? 'Preços gravados na nota — serão aplicados ao cadastro quando ela for confirmada'
                          : 'Preços de venda ainda não atualizados por esta nota'}>
                      {e.precos_atualizados_em
                        ? <span style={{ color: '#15803d', fontWeight: 700 }}>💲✓</span>
                        : e.tem_precos_pendentes
                          ? <span style={{ color: '#b45309', fontWeight: 700 }}>💲⏳</span>
                          : <span style={{ color: '#d97706', fontWeight: 700 }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>) : (<>
            {/* Barra de devoluções */}
            <div style={{ marginBottom: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#666', flex: 1 }}>
                Devoluções geradas a partir de notas de entrada confirmadas — layout usado na conversão fiscal identifica o tipo (devolução de compra, remessa em garantia etc.).
              </span>
              <button className={s.btnErp} onClick={carregarDevolucoes}>Atualizar</button>
            </div>

            <table className={s.itensGrid}>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Cód.</th>
                  <th>Fornecedor</th>
                  <th style={{ width: 90 }}>Entrada Orig.</th>
                  <th style={{ width: 140 }}>Layout</th>
                  <th style={{ width: 100 }}>Data</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Total</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 90 }}>NF-e</th>
                </tr>
              </thead>
              <tbody>
                {devolucoesLista.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888', fontStyle: 'italic' }}>
                      {carregandoDev ? 'Carregando...' : 'Nenhuma devolução gerada — use "📤 NF-e Devolução" numa nota confirmada.'}
                    </td>
                  </tr>
                )}
                {devolucoesLista.map(d => (
                  <tr key={d.id}
                    style={{ cursor: 'pointer' }}
                    className={selDevId === d.id ? s.linhaAtiva : ''}
                    onClick={() => setSelDevId(d.id)}
                    onDoubleClick={() => setDevolucaoAbertaId(d.id)}>
                    <td style={{ textAlign: 'center' }}>{d.id}</td>
                    <td>{d.fornecedor_nome || '—'}</td>
                    <td style={{ textAlign: 'center' }}>#{d.entrada_codigo || d.entrada_nf_id}</td>
                    <td style={{ fontSize: 11.5, color: '#555' }}>{d.layout_nome || '—'}</td>
                    <td>{fmtData(d.criado_em)}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMoeda(parseFloat(d.total || 0) + parseFloat(d.total_ipi || 0))}</td>
                    <td>{renderBadge(d.status)}</td>
                    <td style={{ textAlign: 'center', fontSize: 11.5 }}>{d.nfe_numero || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>)}
        </div>
        <div className={s.statusBar}>
          {tipoLista === 'entradas'
            ? (carregando ? 'Carregando...' : `${lista.length} registro(s)`)
            : (carregandoDev ? 'Carregando...' : `${devolucoesLista.length} devolução(ões)`)}
        </div>
        <div className={s.footer}>
          <div className={s.footerLeft} />
          {tipoLista === 'entradas' ? (
            <div className={s.footerRight}>
              <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                onClick={() => setModalNovo(true)}>Novo</button>
              {selId && (
                <button className={s.btnErp} onClick={() => abrirEntrada(selId)}>Abrir</button>
              )}
              {selId && (
                <button className={s.btnErp} onClick={() => imprimirNF(selId)} disabled={imprimindoNF}>
                  🖨️ Imprimir
                </button>
              )}
              {selId && lista.find(e => e.id === selId)?.status === 'confirmada' && (
                <button className={s.btnErp}
                  onClick={() => setRevisaoFiscalId(selId)}
                  title="Comparar dados fiscais dos produtos com as regras de conversão">
                  Revisão Fiscal
                </button>
              )}
              {selId && (() => {
                const entrada = lista.find(e => e.id === selId);
                const cor = entrada?.precos_atualizados_em ? '#166534' : entrada?.tem_precos_pendentes ? '#b45309' : '#d97706';
                const titulo = entrada?.precos_atualizados_em
                  ? 'Preços desta nota já atualizados em ' + new Date(entrada.precos_atualizados_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) + ' — abrir novamente'
                  : entrada?.tem_precos_pendentes
                    ? 'Preços gravados na nota, pendentes de confirmação — serão aplicados ao cadastro quando ela for confirmada'
                    : 'Simulação de preços dos produtos desta nota: custo de chegada + margem → preço de venda';
                const label = entrada?.precos_atualizados_em ? '💲 Preços ✓'
                  : entrada?.tem_precos_pendentes ? '💲 Preços pendentes'
                  : '💲 Atualizar Preços';
                return (
                  <button className={s.btnErp} style={{ color: cor, fontWeight: 600 }}
                    onClick={() => setModalPrecosId(selId)} title={titulo}>
                    {label}
                  </button>
                );
              })()}
              {selId && lista.find(e => e.id === selId)?.status === 'confirmada'
                && lista.find(e => e.id === selId)?.chave_acesso && (
                <button className={s.btnErp} style={{ color: '#1d4ed8' }}
                  onClick={() => setDevolucaoEntradaId(selId)}
                  title="Devolução de compra: escolher quantidades e layout, e gerar">
                  📤 NF-e Devolução
                </button>
              )}
              <button className={`${s.btnErp} ${s.btnErpPerigo}`}
                onClick={handleExcluir}
                disabled={!selId}>Excluir</button>
              <div className={s.footerSep} />
              <button className={s.btnErp} onClick={() => window.history.back()}>Sair</button>
            </div>
          ) : (
            <div className={s.footerRight}>
              {selDevId && (
                <button className={s.btnErp} onClick={() => setDevolucaoAbertaId(selDevId)}>Abrir</button>
              )}
              <button className={`${s.btnErp} ${s.btnErpPerigo}`}
                onClick={handleExcluirDevolucao}
                disabled={!selDevId || devolucoesLista.find(d => d.id === selDevId)?.status === 'transmitida'}>
                Excluir
              </button>
              <div className={s.footerSep} />
              <button className={s.btnErp} onClick={() => window.history.back()}>Sair</button>
            </div>
          )}
        </div>

        {/* Modal Devolução de Compra — só escolhe quantidades/layout e gera */}
        {devolucaoEntradaId && (
          <ModalDevolucaoCompra
            entradaId={devolucaoEntradaId}
            onClose={(novaDevolucaoId) => {
              setDevolucaoEntradaId(null);
              if (novaDevolucaoId) {
                carregarLista();
                setTipoLista('devolucoes');
                setDevolucaoAbertaId(novaDevolucaoId);
              }
            }}
          />
        )}

        {/* Modal Novo */}
        {modalNovo && (
          <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && setModalNovo(false)}>
            <div className={s.modal} style={{ width: 360 }}>
              <div className={s.modalTitle}>Nova Entrada de Nota Fiscal</div>
              <div className={s.modalBody} style={{ gap: 10 }}>
                <p style={{ margin: '8px 0', fontSize: 13 }}>Como deseja criar a entrada?</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                    style={{ height: 36, fontSize: 14 }}
                    onClick={abrirFormManual}>
                    Entrada Manual
                  </button>
                  <button className={s.btnErp}
                    style={{ height: 36, fontSize: 14 }}
                    onClick={() => xmlInputRef.current?.click()}>
                    Importar XML de NF-e
                  </button>
                  <button className={s.btnErp}
                    style={{ height: 36, fontSize: 14 }}
                    onClick={() => { setModalNovo(false); setModalDfe(true); }}>
                    📡 Importação pelo DF-e (SEFAZ)
                  </button>
                  <input ref={xmlInputRef} type="file"
                    accept=".xml,text/xml,application/xml"
                    style={{ display: 'none' }}
                    onChange={handleXmlFile} />
                </div>
              </div>
              <div className={s.modalFooter}>
                <button className={s.btnErp} onClick={() => setModalNovo(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal DF-e */}
        {modalDfe && (
          <ModalDfe
            onImportar={handleDfeImportar}
            onClose={() => setModalDfe(false)}
          />
        )}
      </div>

      {/* Modal Revisão Fiscal */}
      {revisaoFiscalId && (
        <RevisaoFiscalNF
          entradaId={revisaoFiscalId}
          numeroNF={lista.find(e => e.id === revisaoFiscalId)?.numero_nf || revisaoFiscalId}
          onClose={() => setRevisaoFiscalId(null)}
        />
      )}

      {/* Simulação de preços da nota (💲 Atualizar Preços) */}
      {modalPrecosId && (
        <ModalSimulacaoPrecos
          entradaId={modalPrecosId}
          onClose={() => setModalPrecosId(null)}
          onGravado={(n, aplicadoAgora) => {
            alert(aplicadoAgora
              ? n + ' produto(s) com preço atualizado no cadastro.'
              : n + ' produto(s) com preço gravado na nota — vai pro cadastro quando a nota for confirmada.');
            carregarLista();
          }}
        />
      )}
      </>
    );
  }

  /* ═══════════════════════════════
     VIEW FORM (detalhes)
  ═══════════════════════════════ */
  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}>
        <span>📥</span>
        {form.id ? `Entrada NF #${form.codigo} — ${form.numero_nf || 'S/N'}` : 'Nova Entrada de Nota Fiscal'}
      </div>

      {/* Abas */}
      <div className={s.tabBar}>
        {[
          { id: 'cabecalho',  label: 'Cabeçalho' },
          { id: 'itens',      label: `Itens (${form.itens.length})` },
          { id: 'transporte', label: 'Transporte' },
          { id: 'duplicatas', label: 'Duplicatas' },
        ].map(aba => (
          <button key={aba.id}
            className={`${s.tab} ${abaAtiva === aba.id ? s.tabAtiva : ''}`}
            onClick={() => setAbaAtiva(aba.id)}>
            {aba.label}
          </button>
        ))}
      </div>

      <div className={s.tabContent}>

        {/* ── ABA CABEÇALHO ── */}
        {abaAtiva === 'cabecalho' && (
          <div>
            {/* Fornecedor */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-end' }}>
              <div className={s.campo} style={{ flex: 1 }}>
                <span className={s.campoLabel}>Fornecedor</span>
                <input className={s.inputErp} style={{ flex: 1, minWidth: 0 }}
                  value={form.fornecedor_nome}
                  readOnly
                  placeholder="Clique em ... para selecionar" />
                {editando && (
                  <button className={s.btnLookup} onClick={() => setModalFornec(true)}>...</button>
                )}
              </div>
              <div>
                <span className={s.campoLabel} style={{ minWidth: 'auto' }}>Status</span>
                {' '}{renderBadge(form.status)}
              </div>
            </div>

            {/* NF | Série | Data Emissão | Data Entrada */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <div className={s.campo}>
                <span className={s.campoLabel}>Nº NF</span>
                <input className={s.inputErp} style={{ width: 100 }}
                  value={form.numero_nf}
                  readOnly={!editando}
                  onChange={e => set('numero_nf', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 'auto' }}>Série</span>
                <input className={s.inputErp} style={{ width: 60 }}
                  value={form.serie}
                  readOnly={!editando}
                  onChange={e => set('serie', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 'auto' }}>Dt. Emissão</span>
                <input className={s.inputErp} style={{ width: 120 }}
                  type="date" value={form.data_emissao}
                  readOnly={!editando}
                  onChange={e => set('data_emissao', e.target.value)} />
              </div>
              <div className={s.campo}>
                <span className={s.campoLabel} style={{ minWidth: 'auto' }}>Dt. Entrada</span>
                <input className={s.inputErp} style={{ width: 120 }}
                  type="date" value={form.data_entrada}
                  readOnly={!editando}
                  onChange={e => set('data_entrada', e.target.value)} />
              </div>
            </div>

            {/* Natureza Operação */}
            <div className={s.campo} style={{ marginBottom: 4 }}>
              <span className={s.campoLabel}>Nat. Operação</span>
              <input className={s.inputErp} style={{ width: 340 }}
                value={form.natureza_operacao}
                readOnly={!editando}
                onChange={e => set('natureza_operacao', e.target.value)} />
            </div>

            {/* Chave Acesso */}
            <div className={s.campo}>
              <span className={s.campoLabel}>Chave Acesso</span>
              <input className={s.inputErp} style={{ flex: 1, fontFamily: 'monospace', fontSize: 11 }}
                value={form.chave_acesso}
                readOnly={!editando}
                onChange={e => set('chave_acesso', e.target.value)}
                maxLength={44} />
            </div>

            {/* Totais da NF */}
            <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.5px', marginBottom: 8 }}>
                Totais da Nota Fiscal
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: '4px 16px', alignItems: 'center' }}>
                {[
                  ['Produtos',   'valor_produtos'],
                  ['Frete',      'valor_frete'],
                  ['Seguro',     'valor_seguro'],
                  ['Desconto',   'valor_desconto'],
                  ['IPI',        'valor_ipi'],
                  ['ICMS ST',    'valor_icms_st'],
                  ['ICMS',       'valor_icms'],
                  ['TOTAL NF',   'valor_total'],
                ].map(([label, campo]) => (
                  <React.Fragment key={campo}>
                    <span style={{ fontSize: '0.78rem', fontWeight: campo === 'valor_total' ? 700 : 600, color: 'var(--text-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {label}
                    </span>
                    <input className={`${s.inputErp} ${s.inputErpNum}`}
                      style={{ width: 120, fontWeight: campo === 'valor_total' ? 700 : 'normal', color: campo === 'valor_total' ? 'var(--primary)' : undefined }}
                      value={form[campo]}
                      readOnly={!editando}
                      onChange={e => set(campo, e.target.value)} />
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── ABA ITENS ── */}
        {abaAtiva === 'itens' && (
          <div>
            {editando && (
              <div className={s.itensTool}>
                <button className={`${s.btnErp} ${s.btnErpPrimario}`}
                  onClick={handleAddItem}>+ Adicionar Item</button>
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className={s.itensGrid} style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>#</th>
                    <th style={{ width: 76 }}>Cód. Interno</th>
                    <th style={{ width: 90 }}>Cód. Forn.</th>
                    {editando && <th style={{ width: 28 }}></th>}
                    <th>Produto/Descrição</th>
                    <th style={{ width: 92 }}>NCM</th>
                    <th style={{ width: 52 }}>CFOP</th>
                    <th style={{ width: 42 }}>UN</th>
                    <th style={{ width: 80, textAlign: 'right' }}>Qtde</th>
                    <th style={{ width: 90, textAlign: 'right' }}>Vl Unit</th>
                    <th style={{ width: 90, textAlign: 'right' }}>Vl Total</th>
                    {editando && <th style={{ width: 26 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {form.itens.length === 0 && (
                    <tr>
                      <td colSpan={editando ? 12 : 10}
                        style={{ textAlign: 'center', padding: 16, color: '#888', fontStyle: 'italic' }}>
                        Nenhum item. {editando ? 'Clique em "+ Adicionar Item".' : ''}
                      </td>
                    </tr>
                  )}
                  {form.itens.map((it, idx) => (
                    <tr key={it._key}
                      style={it.produto_inativo && it.produto_id ? { background: '#ffedd5' } : undefined}
                      title={it.produto_inativo && it.produto_id
                        ? (form.status === 'confirmada'
                            ? 'Produto inativo no cadastro'
                            : 'Produto inativo no cadastro — volta a ser ativo ao confirmar a entrada')
                        : undefined}>
                      <td className={s.tdSeq}>{idx + 1}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: it.produto_codigo ? 'var(--primary)' : 'var(--text-muted)', textAlign: 'center' }}>
                        {it.produto_codigo || '—'}
                      </td>
                      <td>
                        <input className={s.cellInput}
                          value={it.codigo_fornecedor}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'codigo_fornecedor', e.target.value)} />
                      </td>
                      {editando && (
                        <td className={s.tdBtn}>
                          <button className={s.btnLookup} style={{ width: 22, padding: 0 }}
                            onClick={() => setModalProdutoIdx(it._key)}
                            title="Vincular produto">...</button>
                        </td>
                      )}
                      <td>
                        <input className={s.cellInput}
                          value={it.produto_nome ? `[${it.produto_nome}] ${it.descricao}` : it.descricao}
                          readOnly={!editando || !!it.produto_id}
                          onChange={e => setItem(it._key, 'descricao', e.target.value)}
                          style={{ color: it.produto_id ? '#003580' : '#000' }} />
                      </td>
                      <td>
                        <input className={s.cellInput}
                          value={it.ncm}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'ncm', e.target.value)} />
                      </td>
                      <td>
                        <input className={s.cellInput}
                          value={it.cfop}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'cfop', e.target.value)} />
                      </td>
                      <td>
                        <input className={s.cellInput}
                          value={it.unidade}
                          readOnly={!editando}
                          style={{ width: 40 }}
                          onChange={e => setItem(it._key, 'unidade', e.target.value)} />
                      </td>
                      <td>
                        <input className={`${s.cellInput} ${s.cellInputNum}`}
                          value={it.quantidade}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'quantidade', e.target.value)} />
                      </td>
                      <td>
                        <input className={`${s.cellInput} ${s.cellInputNum}`}
                          value={it.valor_unitario}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'valor_unitario', e.target.value)} />
                      </td>
                      <td>
                        <input className={`${s.cellInput} ${s.cellInputNum}`}
                          value={it.valor_total}
                          readOnly={!editando}
                          onChange={e => setItem(it._key, 'valor_total', e.target.value)} />
                      </td>
                      {editando && (
                        <td className={s.tdDel}>
                          <button
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c00', fontSize: 14, padding: 0 }}
                            onClick={() => handleRemoveItem(it._key)}
                            title="Remover item">✕</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ABA DUPLICATAS ── */}
        {abaAtiva === 'duplicatas' && (
          <div style={{ padding: '12px 16px' }}>
            {!form.id ? (
              <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', padding: 16 }}>
                Salve a NF para visualizar as duplicatas financeiras.
              </div>
            ) : duplicatasLoad ? (
              <div style={{ color: 'var(--text-muted)', padding: 16 }}>Carregando...</div>
            ) : duplicatas.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', padding: 16 }}>
                Nenhuma conta a pagar vinculada a esta NF.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className={s.itensGrid} style={{ minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th style={{ width: 120 }}>Nº Documento</th>
                      <th style={{ width: 80 }}>Parcela</th>
                      <th style={{ width: 120 }}>Vencimento</th>
                      <th style={{ width: 120, textAlign: 'right' }}>Valor</th>
                      <th>Status</th>
                      <th>Conta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicatas.map((d, i) => (
                      <tr key={d.id}>
                        <td className={s.tdSeq}>{i + 1}</td>
                        <td style={{ fontFamily: 'monospace' }}>{d.numero_documento || '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          {d.parcela && d.total_parcelas ? `${d.parcela}/${d.total_parcelas}` : '—'}
                        </td>
                        <td>{fmtData(d.data_vencimento)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {Number(d.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </td>
                        <td>
                          <span style={{
                            display: 'inline-block', borderRadius: 10, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600,
                            background: d.status === 'pago' ? 'var(--success-bg)' : d.status === 'vencido' ? 'var(--danger-bg)' : 'var(--warning-bg)',
                            color: d.status === 'pago' ? 'var(--success)' : d.status === 'vencido' ? 'var(--danger)' : 'var(--warning)',
                          }}>
                            {d.status === 'pago' ? 'Pago' : d.status === 'vencido' ? 'Vencido' : 'Aberto'}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{d.conta_nome || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── ABA TRANSPORTE ── */}
        {abaAtiva === 'transporte' && (
          <fieldset className={s.fieldset}>
            <legend className={s.legend}>Transporte</legend>
            <div className={s.campo} style={{ marginBottom: 6 }}>
              <span className={s.campoLabel}>Modalidade Frete</span>
              <select className={s.selectErp} style={{ width: 240 }}
                value={form.tipo_frete}
                disabled={!editando}
                onChange={e => set('tipo_frete', e.target.value)}>
                {TIPO_FRETE_OPTS.map(o => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            <div className={s.campo}>
              <span className={s.campoLabel}>Transportadora</span>
              <input className={s.inputErp} style={{ width: 300 }}
                value={form.transportadora}
                readOnly={!editando}
                onChange={e => set('transportadora', e.target.value)} />
            </div>
          </fieldset>
        )}
      </div>

      {/* Status bar */}
      <div className={s.statusBar}>{statusText()}</div>

      {/* Footer */}
      <div className={s.footer}>
        <div className={s.footerLeft} />
        <div className={s.footerRight}>
          {form.id && form.status === 'pendente' && !editando && (
            <button className={`${s.btnErp} ${s.btnErpPrimario}`}
              onClick={() => setModalConfirmar(true)}>
              Confirmar NF
            </button>
          )}
          {form.id && form.status === 'confirmada' && !editando && (
            <button className={s.btnErp}
              onClick={() => setRevisaoFiscalId(form.id)}
              title="Comparar dados fiscais dos produtos com as regras de conversão">
              Revisão Fiscal
            </button>
          )}
          {form.id && form.status === 'confirmada' && !editando && (
            <button className={`${s.btnErp} ${s.btnErpPerigo}`}
              onClick={() => setModalCancelarConf(true)}>
              Cancelar Confirmação
            </button>
          )}
          {form.id && !editando && (
            <button className={s.btnErp} onClick={() => imprimirNF(form.id)} disabled={imprimindoNF}>
              🖨️ Imprimir
            </button>
          )}
          <button className={s.btnErp}
            onClick={handleAlterar}
            disabled={editando || form.status === 'confirmada'}>Alterar</button>
          <button className={s.btnErp}
            onClick={handleCancelar}
            disabled={!editando}>Cancelar</button>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`}
            onClick={handleSalvar}
            disabled={!editando || salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <div className={s.footerSep} />
          <button className={s.btnErp} onClick={() => setModo('lista')}>Voltar</button>
        </div>
      </div>

      {/* Modal Fornecedor */}
      {modalFornec && (
        <ModalBuscaFornecedor
          onClose={() => setModalFornec(false)}
          onSelect={f => {
            set('fornecedor_id', f.id);
            set('fornecedor_nome', f.nome_fantasia || f.razao_social || '');
            setModalFornec(false);
          }}
        />
      )}

      {/* Modal Produto */}
      {modalProdutoIdx !== null && (
        <ModalBuscaProduto
          onClose={() => setModalProdutoIdx(null)}
          onSelect={p => {
            setForm(f => ({
              ...f,
              itens: f.itens.map(it => {
                if (it._key !== modalProdutoIdx) return it;
                return {
                  ...it,
                  produto_id:     p.id,
                  produto_nome:   p.nome,
                  produto_codigo: p.codigo_interno || '',
                  unidade:        p.unidade || it.unidade,
                };
              }),
            }));
            setModalProdutoIdx(null);
          }}
        />
      )}

      {/* Modal Confirmar NF */}
      {modalConfirmar && (
        <div className={s.confirmOverlay}>
          <div className={s.confirmBox}>
            <div className={s.confirmTitulo}>Confirmar Entrada de NF</div>
            <div className={s.confirmCorpo}>
              <p>Confirmar a NF <strong>{form.numero_nf || 'S/N'}</strong>?</p>
              <p>Esta ação não poderá ser desfeita.</p>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12,
                              opacity: tmfSemEstoque ? 0.55 : 1 }}>
                <input type="checkbox"
                  checked={tmfSemEstoque ? false : atualizarEstoque}
                  disabled={tmfSemEstoque}
                  onChange={e => setAtualizarEstoque(e.target.checked)} />
                <span>Atualizar estoque automaticamente</span>
              </label>
              {tmfSemEstoque && (
                <p style={{ marginTop: 6, fontSize: 12, color: '#92400E' }}>
                  ⚠ O tipo de movimento fiscal{form.tipo_movimento_nome ? ` "${form.tipo_movimento_nome}"` : ''} está
                  configurado para <strong>não movimentar estoque</strong>.
                </p>
              )}
            </div>
            <div className={s.confirmBtns}>
              <button className={`${s.btnErp} ${s.btnSim}`}
                onClick={handleConfirmar}
                disabled={confirmando}>
                {confirmando ? 'Confirmando...' : 'Confirmar'}
              </button>
              <button className={`${s.btnErp} ${s.btnNao}`}
                onClick={() => setModalConfirmar(false)}
                disabled={confirmando}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar Confirmação */}
      {modalCancelarConf && (
        <div className={s.confirmOverlay}>
          <div className={s.confirmBox}>
            <div className={s.confirmTitulo}>Cancelar Confirmação da NF</div>
            <div className={s.confirmCorpo}>
              <p>Cancelar a confirmação da NF <strong>{form.numero_nf || 'S/N'}</strong>?</p>
              <p style={{ color: '#b00', marginTop: 8 }}>
                Os movimentos de estoque gerados por esta NF serão <strong>revertidos</strong> automaticamente.
                Após o cancelamento a NF voltará ao status <em>Pendente</em> e poderá ser excluída.
              </p>
            </div>
            <div className={s.confirmBtns}>
              <button className={`${s.btnErp} ${s.btnErpPerigo}`}
                onClick={handleCancelarConfirmacao}
                disabled={cancelando}>
                {cancelando ? 'Cancelando...' : 'Cancelar Confirmação'}
              </button>
              <button className={`${s.btnErp} ${s.btnNao}`}
                onClick={() => setModalCancelarConf(false)}
                disabled={cancelando}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard de Importação de NF-e */}
      {wizardXml && (
        <ImportacaoXMLWizard
          xmlData={wizardXml.data}
          xmlContent={wizardXml.xmlContent}
          onConfirmar={handleWizardConfirmar}
          onCancelar={() => setWizardXml(null)}
        />
      )}

      {/* Modal Revisão Fiscal */}
      {revisaoFiscalId && (
        <RevisaoFiscalNF
          entradaId={revisaoFiscalId}
          numeroNF={lista.find(e => e.id === revisaoFiscalId)?.numero_nf || revisaoFiscalId}
          onClose={() => setRevisaoFiscalId(null)}
        />
      )}
    </div>
  );
}
